import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import CashflowForecastPage from './admin/CashflowForecastPage';
import { FinanceBreakdownDialog } from '../components/FinanceBreakdownDialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    TrendingUp, 
    TrendingDown,
    DollarSign,
    Receipt,
    Wallet,
    CreditCard,
    Server,
    Building,
    Building2,
    Briefcase,
    Megaphone,
    Users,
    Scale,
    MoreHorizontal,
    ArrowRight
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';

import { API_BASE } from '../lib/apiBase';
const API = `${API_BASE}/api`;

// Category colors for the breakdown
const CATEGORY_COLORS = {
    'Subscriptions / Software': 'bg-blue-500',
    'Hosting / Infrastructure': 'bg-purple-500',
    'Banking / Finance Fees': 'bg-emerald-500',
    'One-off Costs': 'bg-amber-500',
    'Professional Services': 'bg-indigo-500',
    'Office / Admin': 'bg-slate-500',
    'Marketing / Sales': 'bg-pink-500',
    'Salaries / Payroll': 'bg-cyan-500',
    'Legal / Accounting': 'bg-orange-500',
    'Miscellaneous': 'bg-gray-500',
};

// Format currency
const formatEur = (amount) => {
    return new Intl.NumberFormat('da-DK', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
    }).format(amount) + ' EUR';
};

// Period selector options
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

export default function FinanceDashboardPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') === 'cashflow' ? 'cashflow' : 'overview';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);

    // Breakdown modal state — shared by the Invoiced and Pipeline cards
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [breakdownVariant, setBreakdownVariant] = useState('invoiced');
    
    // Period selection
    const [periodType, setPeriodType] = useState('month');
    const [year, setYear] = useState(new Date().getFullYear());
    const [periodValue, setPeriodValue] = useState(
        periodType === 'month' ? new Date().getMonth() + 1 : 
        periodType === 'quarter' ? Math.floor(new Date().getMonth() / 3) + 1 : 1
    );

    // Get available years
    const availableYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return [currentYear, currentYear - 1, currentYear - 2];
    }, []);

    useEffect(() => {
        fetchData();
    }, [periodType, year, periodValue]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API}/finance/overview`, {
                headers,
                params: { period_type: periodType, year, period_value: periodValue }
            });
            setOverview(response.data);
        } catch (error) {
            console.error('Failed to fetch financial overview:', error);
        } finally {
            setLoading(false);
        }
    };

    const getPeriodLabel = () => {
        if (periodType === 'month') {
            return `${MONTHS[periodValue - 1]} ${year}`;
        } else if (periodType === 'quarter') {
            return `${QUARTERS[periodValue - 1]} ${year}`;
        } else if (periodType === 'ytd') {
            return `YTD ${year}`;
        } else {
            return `Full Year ${year}`;
        }
    };

    const getPeriodHint = () => {
        if (periodType === 'ytd') return 'Showing actuals up to today — no future projections';
        if (periodType === 'year') return 'Includes projected recurring expenses for the full year';
        return null;
    };

    const netIsPositive = overview?.net_contribution_eur >= 0;

    // Keep ?tab= in sync with active tab and skip overview fetch when on Cashflow tab.
    const handleTabChange = (next) => {
        setActiveTab(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'cashflow') params.set('tab', 'cashflow');
        else params.delete('tab');
        setSearchParams(params, { replace: true });
    };

    return (
        <div className="min-h-screen bg-background" data-testid="finance-dashboard-page">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                Finance
                            </h1>
                            <p className="text-sm text-slate-500 mt-1">
                                Company-wide financial performance and cashflow planning
                            </p>
                        </div>
                        <TabsList data-testid="finance-tabs">
                            <TabsTrigger value="overview" data-testid="finance-tab-overview">Financial Overview</TabsTrigger>
                            <TabsTrigger value="cashflow" data-testid="finance-tab-cashflow">Cashflow Forecast</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="overview" className="mt-0">
                        <FinanceOverviewView
                            loading={loading}
                            overview={overview}
                            periodType={periodType}
                            setPeriodType={setPeriodType}
                            year={year}
                            setYear={setYear}
                            periodValue={periodValue}
                            setPeriodValue={setPeriodValue}
                            availableYears={availableYears}
                            getPeriodLabel={getPeriodLabel}
                            getPeriodHint={getPeriodHint}
                            netIsPositive={netIsPositive}
                            navigate={navigate}
                            openBreakdown={(v) => { setBreakdownVariant(v); setBreakdownOpen(true); }}
                        />
                    </TabsContent>

                    <TabsContent value="cashflow" className="mt-0">
                        <CashflowForecastPage />
                    </TabsContent>
                </Tabs>

                <FinanceBreakdownDialog
                    open={breakdownOpen}
                    onOpenChange={setBreakdownOpen}
                    variant={breakdownVariant}
                    periodType={periodType}
                    year={year}
                    periodValue={periodValue}
                />
            </main>
        </div>
    );
}

function FinanceOverviewView({
    loading, overview, periodType, setPeriodType, year, setYear, periodValue, setPeriodValue,
    availableYears, getPeriodLabel, getPeriodHint, netIsPositive, navigate, openBreakdown,
}) {
    return (
        <>
            {/* Period Selector Row */}
            <div className="flex items-center justify-end mb-4">
                <div className="flex items-center gap-2">
                    <Select value={periodType} onValueChange={(v) => {
                        setPeriodType(v);
                        if (v === 'month') setPeriodValue(new Date().getMonth() + 1);
                        else if (v === 'quarter') setPeriodValue(Math.floor(new Date().getMonth() / 3) + 1);
                        else setPeriodValue(1);
                    }}>
                        <SelectTrigger className="w-36 h-9" data-testid="period-type-selector">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="month">Month</SelectItem>
                            <SelectItem value="quarter">Quarter</SelectItem>
                            <SelectItem value="ytd">Year to Date</SelectItem>
                            <SelectItem value="year">Full Year</SelectItem>
                        </SelectContent>
                    </Select>

                    {periodType === 'month' && (
                        <Select value={periodValue.toString()} onValueChange={(v) => setPeriodValue(parseInt(v))}>
                            <SelectTrigger className="w-32 h-9" data-testid="month-selector">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MONTHS.map((month, idx) => (
                                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{month}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {periodType === 'quarter' && (
                        <Select value={periodValue.toString()} onValueChange={(v) => setPeriodValue(parseInt(v))}>
                            <SelectTrigger className="w-24 h-9" data-testid="quarter-selector">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {QUARTERS.map((q, idx) => (
                                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{q}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                        <SelectTrigger className="w-24 h-9" data-testid="year-selector">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableYears.map((y) => (
                                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Period Hint */}
            {getPeriodHint() && (
                <div className="mb-4 flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="period-hint">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${periodType === 'ytd' ? 'bg-blue-500' : 'bg-amber-500'}`} />
                    <span>{getPeriodLabel()}</span>
                    <span className="text-slate-400">—</span>
                    <span>{getPeriodHint()}</span>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <LoadingSpinner size="lg" />
                </div>
            ) : (
                <>
                        {/* Main Financial Metrics - Combined Total */}
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            {/* Total Revenue */}
                            <Card className="border border-slate-200">
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-slate-100 rounded-lg">
                                            <DollarSign className="h-5 w-5 text-slate-600" />
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900" data-testid="total-revenue-metric">
                                        {formatEur(overview?.total_revenue_eur || 0)}
                                    </p>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">
                                        Total Revenue
                                        <span className="ml-1 text-slate-400">({overview?.active_orders_count || 0} orders)</span>
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Total Costs */}
                            <Card className="border border-slate-200">
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-slate-100 rounded-lg">
                                            <Receipt className="h-5 w-5 text-slate-600" />
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900" data-testid="total-costs-metric">
                                        {formatEur(overview?.total_costs_eur || 0)}
                                    </p>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide mt-1">Total Costs</p>
                                </CardContent>
                            </Card>

                            {/* Total Gross Profit */}
                            <Card className="border border-emerald-200 bg-emerald-50/50">
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-emerald-100 rounded-lg">
                                            <TrendingUp className="h-5 w-5 text-emerald-600" />
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-emerald-700" data-testid="total-gross-profit-metric">
                                        {formatEur(overview?.total_gross_profit_eur || 0)}
                                    </p>
                                    <p className="text-xs text-emerald-600 uppercase tracking-wide mt-1">
                                        Gross Profit ({overview?.total_gross_margin_pct || 0}%)
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Operating Expenses */}
                            <Card className="border border-amber-200 bg-amber-50/50">
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="p-2 bg-amber-100 rounded-lg">
                                            <Wallet className="h-5 w-5 text-amber-600" />
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-amber-700" data-testid="opex-metric">
                                        {formatEur(overview?.operating_expenses_eur || 0)}
                                    </p>
                                    <p className="text-xs text-amber-600 uppercase tracking-wide mt-1">Operating Expenses</p>
                                </CardContent>
                            </Card>

                            {/* Net Contribution */}
                            <Card className={`border ${netIsPositive ? 'border-emerald-300 bg-emerald-100/50' : 'border-red-300 bg-red-100/50'}`}>
                                <CardContent className="pt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`p-2 rounded-lg ${netIsPositive ? 'bg-emerald-200' : 'bg-red-200'}`}>
                                            {netIsPositive ? (
                                                <TrendingUp className="h-5 w-5 text-emerald-700" />
                                            ) : (
                                                <TrendingDown className="h-5 w-5 text-red-700" />
                                            )}
                                        </div>
                                    </div>
                                    <p className={`text-2xl font-bold ${netIsPositive ? 'text-emerald-800' : 'text-red-800'}`} data-testid="net-contribution-metric">
                                        {netIsPositive ? '+' : ''}{formatEur(overview?.net_contribution_eur || 0)}
                                    </p>
                                    <p className={`text-xs uppercase tracking-wide mt-1 ${netIsPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                        Net Contribution ({overview?.net_margin_pct || 0}%)
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Actual vs Pipeline Breakdown */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
                            {/* Actual (Invoiced) */}
                            <Card
                                className="border border-blue-200 bg-blue-50/30 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => openBreakdown && openBreakdown('invoiced')}
                                role="button"
                                data-testid="finance-invoiced-card"
                            >
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-blue-500" />
                                        Invoiced (Actual)
                                        <Badge variant="outline" className="ml-auto text-xs bg-blue-100 text-blue-700 border-blue-200">
                                            {overview?.invoiced_orders_count || 0} orders
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <p className="text-xs text-blue-600 mb-1">Revenue</p>
                                            <p className="text-lg font-semibold text-blue-900">{formatEur(overview?.actual_revenue_eur || 0)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-blue-600 mb-1">Costs</p>
                                            <p className="text-lg font-semibold text-blue-900">{formatEur(overview?.actual_costs_eur || 0)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-blue-600 mb-1">Gross Profit</p>
                                            <p className="text-lg font-semibold text-blue-900">{formatEur(overview?.actual_gross_profit_eur || 0)}</p>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-[11px] text-blue-700/70">Click for order-level breakdown →</p>
                                </CardContent>
                            </Card>

                            {/* Pipeline (Non-invoiced) */}
                            <Card
                                className="border border-purple-200 bg-purple-50/30 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => openBreakdown && openBreakdown('pipeline')}
                                role="button"
                                data-testid="finance-pipeline-card"
                            >
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                                        <div className="h-2 w-2 rounded-full bg-purple-500" />
                                        Pipeline (Uninvoiced)
                                        <Badge variant="outline" className="ml-auto text-xs bg-purple-100 text-purple-700 border-purple-200">
                                            {overview?.pipeline_orders_count || 0} orders
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <p className="text-xs text-purple-600 mb-1">Revenue</p>
                                            <p className="text-lg font-semibold text-purple-900">{formatEur(overview?.pipeline_revenue_eur || 0)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-purple-600 mb-1">Costs</p>
                                            <p className="text-lg font-semibold text-purple-900">{formatEur(overview?.pipeline_costs_eur || 0)}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-purple-600 mb-1">Gross Profit</p>
                                            <p className="text-lg font-semibold text-purple-900">{formatEur(overview?.pipeline_gross_profit_eur || 0)}</p>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-[11px] text-purple-700/70">Click for order-level breakdown →</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Expense Breakdown Card */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="border border-slate-200">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-base font-semibold">
                                            Operating Expenses Breakdown
                                        </CardTitle>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate('/finance/expenses')}
                                            className="text-xs"
                                            data-testid="manage-expenses-btn"
                                        >
                                            Manage Expenses
                                            <ArrowRight className="h-3 w-3 ml-1" />
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {overview?.expenses_by_category && Object.keys(overview.expenses_by_category).length > 0 ? (
                                        <>
                                            {/* Visual Bar */}
                                            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-4">
                                                {Object.entries(overview.expenses_by_category).map(([category, amount]) => {
                                                    const percentage = overview.operating_expenses_eur > 0 
                                                        ? (amount / overview.operating_expenses_eur) * 100 
                                                        : 0;
                                                    return (
                                                        <div
                                                            key={category}
                                                            className={`${CATEGORY_COLORS[category] || 'bg-slate-400'}`}
                                                            style={{ width: `${percentage}%` }}
                                                            title={`${category}: ${formatEur(amount)} (${percentage.toFixed(1)}%)`}
                                                        />
                                                    );
                                                })}
                                            </div>
                                            
                                            {/* Category List */}
                                            <div className="space-y-2">
                                                {Object.entries(overview.expenses_by_category)
                                                    .sort((a, b) => b[1] - a[1])
                                                    .map(([category, amount]) => {
                                                        const percentage = overview.operating_expenses_eur > 0 
                                                            ? (amount / overview.operating_expenses_eur) * 100 
                                                            : 0;
                                                        return (
                                                            <div key={category} className="flex items-center justify-between py-1">
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`h-3 w-3 rounded-full ${CATEGORY_COLORS[category] || 'bg-slate-400'}`} />
                                                                    <span className="text-sm text-slate-700">{category}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-sm font-medium text-slate-900">{formatEur(amount)}</span>
                                                                    <span className="text-xs text-slate-500 w-12 text-right">{percentage.toFixed(1)}%</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-8">
                                            <Wallet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                            <p className="text-sm text-slate-500 mb-3">No expenses recorded for {getPeriodLabel()}</p>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate('/finance/expenses')}
                                            >
                                                Add First Expense
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Summary Stats Card */}
                            <Card className="border border-slate-200">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base font-semibold">
                                        Financial Summary
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                            <span className="text-sm text-slate-600">Active Orders</span>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                    {overview?.invoiced_orders_count || 0} invoiced
                                                </Badge>
                                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                                    {overview?.pipeline_orders_count || 0} pipeline
                                                </Badge>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-3 bg-slate-50 rounded-lg">
                                                <p className="text-xs text-slate-500 mb-1">Gross Margin</p>
                                                <p className="text-lg font-semibold text-slate-900">{overview?.total_gross_margin_pct || 0}%</p>
                                            </div>
                                            <div className="p-3 bg-slate-50 rounded-lg">
                                                <p className="text-xs text-slate-500 mb-1">Net Margin</p>
                                                <p className={`text-lg font-semibold ${netIsPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                                                    {overview?.net_margin_pct || 0}%
                                                </p>
                                            </div>
                                        </div>

                                        <div className="p-3 bg-slate-50 rounded-lg">
                                            <p className="text-xs text-slate-500 mb-2">Profit Flow</p>
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium">{formatEur(overview?.total_revenue_eur || 0)}</span>
                                                <span className="text-slate-400">→</span>
                                                <span className="text-emerald-600">{formatEur(overview?.total_gross_profit_eur || 0)}</span>
                                                <span className="text-slate-400">→</span>
                                                <span className={netIsPositive ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                                    {formatEur(overview?.net_contribution_eur || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
        </>
    );
}
